import { Injectable } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { concatMap, delay, filter, map } from 'rxjs';

import { GroupPickId, PickId } from '../../../core/picks/pick-id';
import { isBetBuilderPickId } from '../../../core/utils';
import BetslipComboBetActions from '../../combo-bet/actions';
import BetslipPickActions from '../../picks/actions';
import { removeAndReplaceGroup, removePicksFromGroup } from '../../picks/grouped-picks.actions';
import { ofTypeReplaceExistingPick } from '../../picks/helpers';
import BetslipSingleBetActions from '../../single-bet/actions';
import BetslipSystemBetActions from '../../system-bet/actions';
import { selectComboPicksState, selectSinglePicksState, selectSystemPicksState } from '../selectors';

@Injectable()
export class PickTypeSharedEffects {
    constructor(
        private actions$: Actions,
        private store: Store,
        private betslipConfig: BetslipConfig,
    ) {}

    //Remove Picks + Child Picks state from all types state when removing a pick from a group
    handleRemovePickAndChildren$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.removePick),
            filter((action) => !action.skipPickTypeStateCleanup),
            map((action) => {
                return BetslipPickActions.removeTypePicks({
                    pickIds: [action.pickId, ...(action.childrenPickIds ?? [])],
                });
            }),
        );
    });

    //Remove Picks state from all types state when removing a pick from a group
    handleRemovePickFromGroup$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(removePicksFromGroup),
            map((action) => {
                return BetslipPickActions.removeTypePicks({
                    pickIds: [...action.pickIds],
                });
            }),
        );
    });

    //Add ISingleBetPickState to Single picks type state when adding a pick.
    handleAddPickSingle$ = createEffect(() => {
        const effect = this;

        return this.actions$.pipe(
            ofType(BetslipPickActions.addPick),
            concatLatestFrom(() => this.store.select(selectSinglePicksState)),
            filter(([action, state]) => !state.picks[action.pick.id.toString()] && !effect.isComboBetBuilderPickId(action.pick.id)),
            map(([action, state]) =>
                BetslipSingleBetActions.addPick({
                    pickId: action.pick.id.toString(),
                    pickState: { order: Object.keys(state.picks).length },
                }),
            ),
        );
    });

    //Add IComboBetPickState to Combo picks type state when adding a pick.
    handleAddPickCombo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.addPick),
            concatLatestFrom(() => this.store.select(selectComboPicksState)),
            filter(([action, state]) => !state.picks[action.pick.id.toString()]),
            map(([action, _]) => BetslipComboBetActions.addPick({ pickId: action.pick.id.toString(), pickState: {} })),
        );
    });

    //Add ISystemBetPickState to System picks type state when adding a pick.
    handleAddPickSystem$ = createEffect(() => {
        const effect = this;

        return this.actions$.pipe(
            ofType(BetslipPickActions.addPick),
            concatLatestFrom(() => this.store.select(selectSystemPicksState)),
            filter(([action, state]) => !state.picks[action.pick.id.toString()] && !effect.isComboBetBuilderPickId(action.pick.id)),
            map(([action, state]) =>
                BetslipSystemBetActions.addPick({
                    pickId: action.pick.id.toString(),
                    pickState: { order: Object.keys(state.picks).length },
                }),
            ),
        );
    });

    //Remove group state from all types state when any pick from the group is removed
    handleRemoveGroupWhenPickIsRemoved$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.removePick),
            filter((action) => !!action.groupInfo),
            map((action) => BetslipComboBetActions.removePick({ pickId: new GroupPickId(action.groupInfo!).toString() })),
        );
    });

    //when a group is removed and replaced with a new pick, the group state should be removed
    handleGroupRemovalFromCombo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(removeAndReplaceGroup),
            map((action) =>
                BetslipComboBetActions.replacePick({
                    newPickId: action.newPick.id.toString(),
                    oldPIckId: action.pickId.toString(),
                }),
            ),
        );
    });

    //Handle adding the new pick state to single type when replace pick is providing a new pick id
    handleReplacePickSingleTypeState$ = createEffect(() => {
        const effect = this;

        return this.actions$.pipe(
            ofTypeReplaceExistingPick(),
            concatLatestFrom(() => this.store.select(selectSinglePicksState)),
            filter(
                ([action, state]) =>
                    !state.picks[action.newPick.id.toString()] &&
                    !action.skipPickTypeStateCleanup &&
                    !effect.isComboBetBuilderPickId(action.newPick.id),
            ),
            concatMap(([action, state]) => {
                const actions: Action[] = [];

                actions.push(
                    BetslipSingleBetActions.addPick({
                        pickId: action.newPick.id.toString(),
                        pickState: {
                            ...state.picks[action.pickId.toString()],
                        },
                    }),
                );
                if (!action.pickId.isEqual(action.newPick.id) && !action.atBetPlacement) {
                    actions.push(BetslipSingleBetActions.addPickWithNewPickId());
                }

                return actions;
            }),
        );
    });

    //Handle adding the new pick state to combo type when replace pick is providing a new pick id
    handleReplacePickComboTypeState$ = createEffect(() => {
        return this.actions$.pipe(
            ofTypeReplaceExistingPick(),
            concatLatestFrom(() => this.store.select(selectComboPicksState)),
            filter(([action, state]) => !state.picks[action.newPick.id.toString()]),
            concatMap(([action, state]) => {
                const actions: Action[] = [];

                actions.push(
                    BetslipComboBetActions.addPick({
                        pickId: action.newPick.id.toString(),
                        pickState: { ...state.picks[action.pickId.toString()] },
                    }),
                );
                if (!action.pickId.isEqual(action.newPick.id) && !action.atBetPlacement) {
                    actions.push(BetslipComboBetActions.addPickWithNewPickId());
                }

                return actions;
            }),
        );
    });

    //Handle adding the new pick state to system type when replace pick is providing a new pick id
    handleReplacePickSystemTypeState$ = createEffect(() => {
        const effect = this;

        return this.actions$.pipe(
            ofTypeReplaceExistingPick(),
            concatLatestFrom(() => [this.store.select(selectSystemPicksState)]),
            filter(
                ([action, state]) =>
                    !state.picks[action.newPick.id.toString()] &&
                    !action.skipPickTypeStateCleanup &&
                    !effect.isComboBetBuilderPickId(action.newPick.id),
            ),
            concatMap(([action, state]) => {
                const actions: Action[] = [];

                actions.push(
                    BetslipSystemBetActions.addPick({
                        pickId: action.newPick.id.toString(),
                        pickState: { ...state.picks[action.pickId.toString()] },
                    }),
                );
                if (!action.pickId.isEqual(action.newPick.id) && !action.atBetPlacement) {
                    actions.push(BetslipSystemBetActions.addPickWithNewPickId());
                }

                return actions;
            }),
        );
    });

    //Handle removing the old pick state from single type when replace pick is providing a new pick id
    handleReplacePickRemoveSingleTypeState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.replacePick),
            delay(5),
            filter((action) => action.pickId.toString() !== action.newPick.id.toString()),
            concatLatestFrom(() => this.store.select(selectSinglePicksState)),
            filter(([action, state]) => !!state.picks[action.pickId.toString()] && !action.skipPickTypeStateCleanup),
            map(([action, _]) => BetslipSingleBetActions.requestRemovePick({ pickId: action.pickId.toString() })),
        );
    });

    //Handle removing the old pick state from combo type when replace pick is providing a new pick id
    handleReplacePickRemoveComboTypeState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.replacePick),
            filter((action) => action.pickId.toString() !== action.newPick.id.toString()),
            concatLatestFrom(() => this.store.select(selectComboPicksState)),
            filter(([action, state]) => !!state.picks[action.pickId.toString()] && !action.skipPickTypeStateCleanup),
            map(([action, _]) => BetslipComboBetActions.removePick({ pickId: action.pickId.toString() })),
        );
    });

    //Handle removing the old pick state from system type when replace pick is providing a new pick id
    handleReplacePickRemoveSystemTypeState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.replacePick),
            filter((action) => action.pickId.toString() !== action.newPick.id.toString()),
            concatLatestFrom(() => this.store.select(selectSystemPicksState)),
            filter(([action, state]) => !!state.picks[action.pickId.toString()] && !action.skipPickTypeStateCleanup),
            map(([action, _]) => BetslipSystemBetActions.requestRemovePick({ pickId: action.pickId.toString() })),
        );
    });

    handleSingleStateReorder$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipSingleBetActions.requestRemovePick),
            concatLatestFrom(() => this.store.select(selectSinglePicksState)),
            filter(([action, state]) => !!state.picks[action.pickId]),
            concatMap(([action, state]) => {
                const removedOrder = state.picks[action.pickId]?.order ?? 0;
                const actions = [BetslipSingleBetActions.removePick({ pickId: action.pickId })];
                actions.push(
                    ...Object.entries(state.picks)
                        .filter(([_, value]) => value.order > removedOrder)
                        .map(([key, value]) =>
                            BetslipSingleBetActions.updateOrder({
                                pickId: key,
                                order: value.order - 1,
                            }),
                        ),
                );

                // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
                return actions;
            }),
        );
    });

    handleSystemStateReorder$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipSystemBetActions.requestRemovePick),
            concatLatestFrom(() => this.store.select(selectSystemPicksState)),
            filter(([action, state]) => !!state.picks[action.pickId]),
            concatMap(([action, state]) => {
                const removedOrder = state.picks[action.pickId]?.order ?? 0;
                const actions = [BetslipSystemBetActions.removePick({ pickId: action.pickId })];
                actions.push(
                    ...Object.entries(state.picks)
                        .filter(([_, value]) => value.order > removedOrder)
                        .map(([key, value]) =>
                            BetslipSystemBetActions.updateOrder({
                                pickId: key,
                                order: value.order - 1,
                            }),
                        ),
                );

                // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
                return actions;
            }),
        );
    });

    private isComboBetBuilderPickId(pickId: PickId): boolean {
        if (this.betslipConfig.isSportcastAsComboEnabled) {
            return isBetBuilderPickId(pickId);
        }

        return GroupPickId.isId(pickId);
    }
}
